/**
 * TOAST: Generic error component displayed when an API call fails.
 */
import React from "react";
import { Button } from "react-bootstrap";
import useAppInsightsTracking from "hooks/useAppInsightsTracking";
import { ReactComponent as EBBErrorImg } from "assets/images/ebb_bros_cutout.svg";
import { ReactComponent as BRUErrorImg } from "assets/images/bb_logo.svg";
import { ReactComponent as NNYBErrorImg } from "assets/images/nnyb_logo.svg";
import getBrandName from "app/shared/utils/BrandHelper";
import Constants from "app/shared/constants/constants";
import "./ErrorScreenStyles.scss";

interface ErrorScreenProps {
    title: string;
    description: string;
    primaryActionLabel: string;
    primaryAction: () => void;
    secondaryActionLabel?: string;
    secondaryAction?: () => void;
    isPrimaryActionLoading?: boolean;
}

function ErrorScreen({
    title,
    description,
    primaryActionLabel,
    primaryAction,
    secondaryActionLabel,
    secondaryAction,
    isPrimaryActionLoading = false,
}: ErrorScreenProps) {
    useAppInsightsTracking("ErrorState");

    const { brandName } = getBrandName();

    const errorImages: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
        [Constants.EINSTEIN_APP]: EBBErrorImg,
        [Constants.BRUEGGERS_APP]: BRUErrorImg,
        [Constants.NOAH_APP]: NNYBErrorImg,
    };

    const ErrorImage = errorImages[brandName] ?? EBBErrorImg;

    return (
        <div className="error-screen-content d-flex justify-content-center">
            <div className="text-center error-screen-container">
                <ErrorImage
                    className="error-screen-image"
                    role="img"
                    aria-label={`${brandName} error`}
                />

                <div className="fw-bold">{title}</div>
                <div>{description}</div>

                <div className="error-screen-actions d-flex flex-column gap-3 mt-4">
                    <Button
                        className="text-uppercase order-now-card-button w-100 m-0"
                        onClick={primaryAction}
                        disabled={isPrimaryActionLoading}
                    >
                        {primaryActionLabel}
                    </Button>

                    {secondaryActionLabel && secondaryAction && (
                        <Button
                            className="tertiary-button text-uppercase w-100 m-0"
                            onClick={secondaryAction}
                        >
                            {secondaryActionLabel}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ErrorScreen;
